import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sendMessage } from '../services/api';

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await sendMessage(inputValue.trim());
      const botMessage = {
        type: 'bot',
        content: response.message,
        sources: response.sources || [],
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        type: 'bot',
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleExampleClick = (question) => {
    setInputValue(question);
  };

  const exampleQuestions = [
    "What is TapMyBack?",
    "What are TapMyBack's pricing plans?",
    "Does TapMyBack integrate with Slack?",
    "How does employee recognition work?",
    "What features does TapMyBack offer?"
  ];

  return (
    <div className="chat-container">
      <div className="chat-window">
        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="welcome-message">
              <h3>Welcome to TapMyBack Assistant! 👋</h3>
              <p>I'm here to help you learn about TapMyBack's employee recognition platform.</p>
              <p>I can answer questions about features, pricing, integrations, and more!</p>
              <p>All my information comes directly from TapMyBack's official website.</p>
              
              <div className="example-questions">
                <p style={{ width: '100%', marginBottom: '10px', fontWeight: '500' }}>
                  Try asking:
                </p>
                {exampleQuestions.map((question, index) => (
                  <div
                    key={index}
                    className="example-question"
                    onClick={() => handleExampleClick(question)}
                  >
                    {question}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <div key={index} className={`message ${message.type}`}>
                <div className="message-content">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({node, ...props}) => <p style={{margin: '10px 0', lineHeight: '1.6'}} {...props} />,
                      strong: ({node, ...props}) => <strong style={{fontWeight: 600, color: '#333'}} {...props} />,
                      em: ({node, ...props}) => <em style={{fontStyle: 'italic'}} {...props} />,
                      ul: ({node, ...props}) => <ul style={{margin: '10px 0', paddingLeft: '25px'}} {...props} />,
                      ol: ({node, ...props}) => <ol style={{margin: '10px 0', paddingLeft: '25px'}} {...props} />,
                      li: ({node, ...props}) => <li style={{margin: '6px 0', paddingLeft: '5px'}} {...props} />,
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                  {message.sources && message.sources.length > 0 && (
                    <div className="message-sources">
                      <strong>Sources:</strong>
                      {message.sources.map((source, idx) => (
                        <a
                          key={idx}
                          href={source}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {source}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          
          {isLoading && (
            <div className="message bot">
              <div className="message-content">
                <div className="loading">
                  <span>Thinking</span>
                  <div className="loading-dots"></div>
                  <div className="loading-dots"></div>
                  <div className="loading-dots"></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-container">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about TapMyBack..."
            className="message-input"
            disabled={isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="send-button"
          >
            {isLoading ? <Loader size={16} /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;



